// Colour palettes for different vision types
// Designed with distinct luminance values for maximum distinguishability

export type VisionType = 'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia';

export interface ColourPalette {
  zones: Record<number, string>;
  heatmap: { low: string; mid: string; high: string };
  accent: string;
  chart: string[];
}

// Normal vision (current default)
const NORMAL: ColourPalette = {
  zones: { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#3b82f6', 5: '#22c55e' },
  heatmap: { low: '#22c55e', mid: '#eab308', high: '#ef4444' },
  accent: '#1db954',
  chart: ['#ef4444', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#8b5cf6', '#ec4899'],
};

// Protanopia (red-weak, ~1.3% of men)
// Reds appear dark/brownish. Replace reds with blues, keep luminance distinct.
const PROTANOPIA: ColourPalette = {
  zones: { 1: '#1e40af', 2: '#d97706', 3: '#eab308', 4: '#06b6d4', 5: '#8b5cf6' },
  heatmap: { low: '#06b6d4', mid: '#eab308', high: '#1e40af' },
  accent: '#06b6d4',
  chart: ['#1e40af', '#d97706', '#eab308', '#06b6d4', '#8b5cf6', '#ec4899', '#64748b'],
};

// Deuteranopia (green-weak, ~1.2% of men, most common)
// Greens appear yellowish. Avoid red-green distinction.
const DEUTERANOPIA: ColourPalette = {
  zones: { 1: '#1e40af', 2: '#d97706', 3: '#fbbf24', 4: '#06b6d4', 5: '#a855f7' },
  heatmap: { low: '#06b6d4', mid: '#fbbf24', high: '#1e40af' },
  accent: '#06b6d4',
  chart: ['#1e40af', '#d97706', '#fbbf24', '#06b6d4', '#a855f7', '#ec4899', '#64748b'],
};

// Tritanopia (blue-weak, rare ~0.01%)
// Blues appear greenish. Avoid blue-yellow distinction.
const TRITANOPIA: ColourPalette = {
  zones: { 1: '#ef4444', 2: '#f97316', 3: '#a3a3a3', 4: '#22c55e', 5: '#ec4899' },
  heatmap: { low: '#22c55e', mid: '#a3a3a3', high: '#ef4444' },
  accent: '#22c55e',
  chart: ['#ef4444', '#f97316', '#a3a3a3', '#22c55e', '#ec4899', '#8b5cf6', '#64748b'],
};

// Achromatopsia (total colour blindness, very rare)
// Pure luminance-based. Use patterns/shapes as primary differentiator.
const ACHROMATOPSIA: ColourPalette = {
  zones: { 1: '#1a1a1a', 2: '#4a4a4a', 3: '#808080', 4: '#b0b0b0', 5: '#e0e0e0' },
  heatmap: { low: '#e0e0e0', mid: '#808080', high: '#1a1a1a' },
  accent: '#808080',
  chart: ['#1a1a1a', '#4a4a4a', '#606060', '#808080', '#a0a0a0', '#c0c0c0', '#e0e0e0'],
};

const PALETTES: Record<VisionType, ColourPalette> = {
  normal: NORMAL,
  protanopia: PROTANOPIA,
  deuteranopia: DEUTERANOPIA,
  tritanopia: TRITANOPIA,
  achromatopsia: ACHROMATOPSIA,
};

export const VISION_TYPE_LABELS: Record<VisionType, string> = {
  normal: 'Normal Vision',
  protanopia: 'Protanopia (red-weak)',
  deuteranopia: 'Deuteranopia (green-weak)',
  tritanopia: 'Tritanopia (blue-weak)',
  achromatopsia: 'Achromatopsia (no colour)',
};

export const VISION_TYPES: VisionType[] = ['normal', 'protanopia', 'deuteranopia', 'tritanopia', 'achromatopsia'];

export const getColourPalette = (visionType: VisionType): ColourPalette => {
  return PALETTES[visionType] || PALETTES.normal;
};

export const STORAGE_KEY = 'colour-vision-type';

export const getSavedVisionType = (): VisionType => {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved && saved in PALETTES) return saved as VisionType;
  return 'normal';
};

export const saveVisionType = (type: VisionType) => {
  localStorage.setItem(STORAGE_KEY, type);
};
